import {
  ComponentProps,
  FocusEvent,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from "react";
import { autoUpdate, flip, shift, useFloating } from "@floating-ui/react";
import {
  EditableText,
  FloatingControls,
  useFloatingControls,
  mergeRefs
} from "@theorgchartdev/storybook";

type EditableTextProps = Omit<ComponentProps<typeof EditableText>, "onBlur" | "skipInputBlur">;

export type EditableTextWithFloatingControlsProps = EditableTextProps & {
  align?: "left" | "right";
  saveOnBlur?: boolean;
};

type EditableTextHandle = {
  setEditMode: (editMode: boolean) => void;
  inputRef: React.RefObject<HTMLInputElement>;
  containerRef: React.RefObject<HTMLDivElement>;
};

type RefAttrs = {
  setEditMode: (editMode: boolean) => void;
};

const EditableTextWithFloatingControls = forwardRef<
  RefAttrs,
  EditableTextWithFloatingControlsProps
>((props, ref) => {
  const {
    align = "left",
    saveOnBlur = false,
    onCancel,
    onChange,
    onEditModeChange,
    validatorFn,
    initEditMode,
    ...rest
  } = props;

  const [editMode, setEditModeLocal] = useState(initEditMode ?? false);
  const editableRef = useRef<EditableTextHandle>(null);

  // We do not use active/activate since EditableText manages its own edit-mode state.
  const { setDraft, confirm, cancel } = useFloatingControls(rest.text, value =>
    onChange(value, rest.eventInfo)
  );

  const [validResult, setValidResult] = useState(
    () => !validatorFn || validatorFn(rest.text).result
  );

  // Use the given validatorFn, but wrap it to also update local validResult state on each call.
  const wrappedValidatorFn = (text: string) => {
    setDraft(text);
    // Return the full shape so the return type is consistent on both branches.
    const result = validatorFn?.(text) ?? {
      result: true,
      msg: "",
      status: undefined,
      showText: false
    };
    setValidResult(result.result);
    return result;
  };

  // Positioning
  const floatingContainerRef = useRef<HTMLDivElement>(null);
  const { refs: floatingRefs, floatingStyles } = useFloating({
    strategy: "fixed",
    placement: align === "right" ? "bottom-end" : "bottom-start",
    whileElementsMounted: autoUpdate,
    middleware: [flip(), shift()]
  });

  useEffect(() => {
    if (!editMode) return;
    if (editableRef.current?.inputRef.current) {
      floatingRefs.setReference(editableRef.current.inputRef.current);
    }
    // Reseed draft and validResult when entering edit mode.
    setDraft(rest.text);
    setValidResult(!validatorFn || validatorFn(rest.text).result);
  }, [editMode]);

  const handleEditModeChange = (editing: boolean) => {
    setEditModeLocal(editing);
    onEditModeChange?.(editing);
  };

  const handleConfirm = () => {
    if (!validResult) return;
    confirm();
    editableRef.current?.setEditMode(false);
  };

  const handleCancel = () => {
    cancel();
    onCancel?.();
    editableRef.current?.setEditMode(false);
  };

  // Blur guard: only act when focus truly leaves both EditableText's container
  // and the floating container.
  const handleContainerBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (!editMode) return;
    const focusLeft =
      event.relatedTarget === null ||
      (!editableRef.current?.containerRef?.current?.contains(event.relatedTarget as Node) &&
        !floatingContainerRef.current?.contains(event.relatedTarget as Node));

    if (focusLeft) {
      if (saveOnBlur && validResult) {
        confirm();
      } else {
        cancel();
        onCancel?.();
      }
      editableRef.current?.setEditMode(false);
    }
  };

  useImperativeHandle(ref, () => ({ setEditMode: v => editableRef.current?.setEditMode(v) }), []);

  return (
    <>
      <EditableText
        ref={editableRef}
        {...rest}
        initEditMode={initEditMode}
        validatorFn={wrappedValidatorFn}
        onChange={onChange}
        onCancel={onCancel}
        onEditModeChange={handleEditModeChange}
        onBlur={handleContainerBlur}
        skipInputBlur
      />
      {editMode && (
        <div
          ref={mergeRefs(floatingRefs.setFloating, floatingContainerRef)}
          style={{ ...floatingStyles, zIndex: 1000 }}
          onBlur={handleContainerBlur}
        >
          <FloatingControls
            onConfirm={handleConfirm}
            onCancel={handleCancel}
            handleBlurBackward={() => editableRef.current?.inputRef.current?.focus()}
            disableConfirm={!validResult}
            align={align}
          />
        </div>
      )}
    </>
  );
});

EditableTextWithFloatingControls.displayName = "EditableTextWithFloatingControls";

export default EditableTextWithFloatingControls;
